
import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Typography, Button, makeStyles, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';

import { PageTitle } from '../../components/PageTitle';
import { AuthenticationService, RegisterAccountRequest } from '../../services/AuthenticationService';
import { FieldValidationError } from '../../services/server/ServerResponses';
import { useUser } from '../../utilities/useUser';
import { useIsMobile } from '../../components/Mui3WidthProvider';
import { routes } from '../../routes';
import CheckMark from '@material-ui/icons/Check';
import LockOutlineIcon from '@material-ui/icons/LockOutlined';

import { useHistory } from "react-router-dom";
import { useQueryParams } from "../../components/useQueryParams";
import { RegistrationForm, RegistrationFormRequest } from './Components/RegistrationForm';
import { PricingTable } from './Components/PricingTable';
import { SubscriptionPlan, Subscriptionterm } from '../../entities/SubscriptionPlan';
import { LoadingPage } from '../../components/LoadingPage';
import Coupon from '../../entities/Coupon';
import { SubscriptionService } from '../../services/SubscriptionService';
import { TermsAndConditions } from './Components/TermsAndConditions';
import { ApplicationUser } from '../../entities/ApplicationUser';

const MoneyFormater = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 4
});

export function NewRegister(){
    const userInfo = useUser();
    const history = useHistory();
    const isMobile = useIsMobile();
    const query = useQueryParams();
    const classes = useStyles();

    const [isLoading, setIsLoading] = useState(true);
    const [disabled, setDisabled] = useState(false);
    const [errors, setErrors] = useState<FieldValidationError[]>([]);
    const [formValues, setFormValues] = useState<RegistrationFormRequest>({
        fullName: query.get("fullname") ?? "",
        email: query.get("email") ?? "",
        password: ""
    });

    const [activePlans, setActivePlans] = useState<SubscriptionPlan[]>([]);
    const [selectedPlanTerm, setSelectedPlanTerm] = useState<Subscriptionterm>(query.get("monthly") === 'true' ? Subscriptionterm.Monthly : Subscriptionterm.Annual)
    const [couponCode, setCouponCode] = useState<Coupon | null>(null);
    const [hasUserHadTrial, setHasUserHadTrial] = useState(false);
    const user = userInfo.onlineUser

    const isMonthly = useMemo(() => selectedPlanTerm === Subscriptionterm.Monthly, [selectedPlanTerm])

    useEffect(() => {
        async function loadData(){
            setIsLoading(true);

            const promoCode = query.get("promocode");
            if(promoCode){
                const promoCodeResponse = await SubscriptionService.getPromoCode(promoCode);

                if(promoCodeResponse.success) setCouponCode(promoCodeResponse.data);
            }
            if(user){
                const canHaveTrialResponse = await SubscriptionService.hasUserHadFreeTrial()

                if(canHaveTrialResponse.success) setHasUserHadTrial(canHaveTrialResponse.data);
            }

            const planResponse = await SubscriptionService.getActivePlans();

            if(planResponse.success){
                setActivePlans(planResponse.data);
            }

            setIsLoading(false)
        }

        loadData();
    }, [])

    const selectedPlan = useMemo(() => activePlans.find(a => a.subscriptionTerm === selectedPlanTerm), [selectedPlanTerm, activePlans])
    const couponValue = useMemo(() => {
        if(!couponCode)
            return undefined;

        if (couponCode.isPercentage && couponCode.percentOff && selectedPlan)              
            return MoneyFormater.format(selectedPlan.price * couponCode.percentOff);

        if (couponCode.amountOff)
            return MoneyFormater.format(couponCode.amountOff);

    }, [couponCode, selectedPlan, activePlans])
    const costOfPlan = useMemo(() => {
        if(!selectedPlan)
            return "$0.00";

        const currentPrice =selectedPlan.price;

        if(!couponCode)
            return MoneyFormater.format(currentPrice);

        if(couponCode.amountOff)
            return MoneyFormater.format(currentPrice - couponCode.amountOff);

        if(couponCode.percentOff && couponCode.percentOff === 1)   
            return MoneyFormater.format(0); 

        if(couponCode.percentOff)
            return MoneyFormater.format(currentPrice - (currentPrice * couponCode.percentOff));
        
        return MoneyFormater.format(currentPrice);
    }, [couponCode, selectedPlan, activePlans])

    const handleCreateUser = async():Promise<ApplicationUser | null> => {
        localStorage.setItem("registerParams", history.location.search)

        if(formValues.email.trim().length === 0){
            setErrors([{field: "Email", errors: ["Invalid Email Address"]}]);
            return null;
        }

        const splitName = formValues.fullName.trim().split(' ');

        if (splitName.length < 2){
            setErrors([{ field: "FullName", errors: ['First and Last name are required'] }]);
            return null;
        }

        const lastName = splitName[splitName.length - 1].trim();
        const firstName = splitName.slice(0, splitName.length - 1).join(' ').trim();    

        const request:RegisterAccountRequest = {
            firstName,
            lastName,
            email: formValues.email,
            password: formValues.password
        }

        setDisabled(true);
        const response = await AuthenticationService.register(request);
        setDisabled(false);

        if(response.error) return null;
        if(response.validation){
            setErrors(response.errors);
            return null
        }

        return response.data
    }

    const handleFormSubmit = async(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        if (disabled || !selectedPlan)
            return;

        const currentUser = user ?? await handleCreateUser();

        if(!currentUser)
            return;

        userInfo.updateUser(currentUser);        
        const sessionRequest = {
            priceId: selectedPlan.id, 
            couponId: couponCode?.id, 
            cancelPath: routes.public.reqisterPage, 
            isAnnualPlan: selectedPlanTerm === Subscriptionterm.Annual
        }

        setDisabled(true);
        const sessionResponse = await SubscriptionService.createCheckoutSession(sessionRequest);
        setDisabled(false);

        if(sessionResponse.success)
            return history.push(routes.app.resolve.checkoutPage(sessionResponse.data.sessionId))
    }

    if(isLoading || !selectedPlan)
        return (
            <LoadingPage />
        );

    if(isMobile)
        return(
            <form onSubmit={handleFormSubmit}>
                <PageTitle title={ user ? "Sermon Maker - Activate" : "Sermon Maker - Register"} />
                <Grid style={{ padding: 24, overflowY: "scroll"}} container>
                    <Grid item xs={12} container>
                        <Typography variant="h4" style={{ fontSize: "1.7em" }} gutterBottom>
                            Get Started Now <LockOutlineIcon style={{ opacity: .6 }} fontSize="small" />
                        </Typography>
                        { !user &&
                            <RegistrationForm errors={errors} setForm={(form) => setFormValues(form)} formRequest={formValues} />
                        }
                    </Grid>
                    <Grid item xs={12} container>
                        <PricingTable 
                            onChangePlan={(term) => setSelectedPlanTerm(term)}
                            selectedPlan={selectedPlan}
                            costOfPlan={costOfPlan}
                            couponValue={couponValue}
                            disabled={disabled}
                            trialAllowed={!hasUserHadTrial} 
                        />
                    </Grid>
                    <Grid item xs={12} style={{ marginTop: 5, marginBottom: 5}}>
                        <Button
                            type="submit"
                            disabled={disabled}
                            className={classes.submitButton}
                        >
                            <Typography variant="h6">
                                { user && isMonthly && 'Start Now' }
                                { !user && isMonthly && 'Create My Account'}
                                { !isMonthly && 'Start My Free Trial' }
                            </Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={12} container >
                        <TermsAndConditions isMonthly={isMonthly} costOfPlan={costOfPlan} />
                    </Grid>
                </Grid>
            </form>
    )

    return (
        <Grid style={{height: "100vh" }} container justifyContent="center" alignItems="center">
            <PageTitle title={ user ? "Sermon Maker - Activate" : "Sermon Maker - Register"} />
            <Grid item md={5} lg={5} style={{ backgroundColor: "#ececec", height: "100%"}}>
                <Grid container direction="row-reverse" style={{ paddingTop: "10vh", paddingRight: 20}} >
                    <Grid item md={11} lg={8} xl={5} container>
                        <Grid item>
                            <Typography variant="h5" gutterBottom>
                                <div style={{ display: "inline-flex", marginLeft: 3 }}>
                                    <img style={{ width: 30, marginRight: 6}} src="//i.cdn-sc.com/Logos/sclogo-grayscale.png" alt="SermonCentral" /> Sermon Maker
                                </div>
                            </Typography>
                        </Grid>
                        <PricingTable 
                            onChangePlan={(term) => setSelectedPlanTerm(term)}
                            selectedPlan={selectedPlan}
                            costOfPlan={costOfPlan}
                            couponValue={couponValue}
                            disabled={disabled}
                            trialAllowed={!hasUserHadTrial} 
                        />
                         <div style={{ paddingTop: 24, paddingLeft: 17, marginTop:  6 }}>
                            <Typography variant="subtitle1">
                                Your Subscription Also Includes
                            </Typography>
                            <List dense>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckMark/>
                                    </ListItemIcon>
                                    <ListItemText>Access to all time-saving features</ListItemText>
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckMark/>
                                        </ListItemIcon>
                                    <ListItemText>Full access to all premium templates</ListItemText>
                                </ListItem>
                                <ListItem>
                                    <ListItemIcon>
                                        <CheckMark/>
                                    </ListItemIcon>
                                    <ListItemText>Automatic subscription renewal for 24/7 uninterrupted service</ListItemText>
                                </ListItem>
                            </List>
                        </div>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item md={7} lg={7} style={{ backgroundColor: "#FFF", height: "100%"}}>
                <form onSubmit={handleFormSubmit}>
                    <Grid container direction="row" style={{ paddingTop: "10vh", paddingLeft: 20}} >
                        <Grid item md={11} lg={9} xl={6} container>
                            <Grid item xs={12}>
                                <Typography variant="h4" style={{ fontSize: "2.125rem" }} gutterBottom>
                                    Get Started Now <LockOutlineIcon style={{ opacity: .6 }} fontSize="small" />
                                </Typography>
                            </Grid>
                            { !user &&
                                <RegistrationForm errors={errors} setForm={(form) => setFormValues(form)} formRequest={formValues} />
                            }
                            <Grid style={{ marginTop: 5, marginBottom: 5}} item xs={12}>
                                <Button
                                    type="submit"
                                    disabled={disabled}
                                    className={classes.submitButton}
                                    >
                                    <Typography variant="h6">
                                        { user && isMonthly && 'Start Now' }
                                        { !user && isMonthly && 'Create My Account'}
                                        { !isMonthly && 'Start My Free Trial' }
                                    </Typography>
                                </Button>
                            </Grid>
                            <div style={{ paddingTop: 24, marginTop:  6 }}>
                                <TermsAndConditions costOfPlan={costOfPlan} isMonthly={isMonthly} />
                            </div>
                        </Grid>
                    </Grid>
                </form>
            </Grid>
        </Grid>
    );
}


const useStyles = makeStyles((theme) => ({
    submitButton: {
        height: 60,
        width: "100%",
        color: "#FFF",
        backgroundColor: "rgb(50, 153, 57)",
        fontSize: "24px",
        "&:hover": {
            backgroundColor: "rgb(50, 153, 57, .8)",
        },
        "&:disabled": {
            backgroundColor: "rgb(50, 153, 57, .5)",
        },
    },
}))